<template>
    <div name="insulin-select-modal" class="wrapper">
        <div class="dim" @click="close"></div>
        <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="insulin-select-title">
            <div class="header">
                <div class="col">
                    <span id="insulin-select-title" class="title">인슐린 선택</span>
                    <span class="subtitle">찾으시는 인슐린을 모두 선택해 주세요.</span>
                </div>
                <button type="button" class="close-button" aria-label="닫기" @click="close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 6l12 12M18 6L6 18" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <div class="search">
                <svg class="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="9" cy="9" r="6.25" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M13.6 13.6L17 17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                <!-- 한글 조합 중에도 바로 검색되도록 v-model 대신 input 이벤트 사용 -->
                <input
                    class="search-input"
                    type="text"
                    :value="keyword"
                    @input="keyword = $event.target.value"
                    placeholder="인슐린 이름 검색"
                    :disabled="isLoading || isError"
                />
                <button v-if="keyword !== ''" type="button" class="clear-button" aria-label="검색어 지우기" @click="keyword = ''">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="8" fill="#A3AAB6"/>
                        <path d="M7.2 7.2l5.6 5.6M12.8 7.2l-5.6 5.6" stroke="#FFF" stroke-width="1.6" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <!-- 선택된 약품: 터치는 스와이프, 마우스는 클릭한 채로 끌어서 좌우 이동 -->
            <div v-if="innerSelected.length > 0" class="selected-wrap">
                <div
                    ref="selectedList"
                    :class="['selected-list', { 'selected-list-dragging': chipDrag.isDragging }]"
                    @scroll="updateChipEdges"
                    @mousedown="onChipMouseDown"
                    @wheel="onChipWheel"
                    @click.capture="onChipClickCapture"
                >
                    <button
                        v-for="name in innerSelected"
                        :key="`selected_${name}`"
                        type="button"
                        class="selected-chip"
                        :aria-label="`${name} 선택 해제`"
                        @click="toggle(name)"
                    >
                        {{ name }}
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <!-- 양옆에 더 있는 경우 흐리게 표시 -->
                <div :class="['edge', 'edge-left', { 'edge-visible': chipEdges.left }]"></div>
                <div :class="['edge', 'edge-right', { 'edge-visible': chipEdges.right }]"></div>
            </div>

            <div class="summary">
                <span>
                    {{ normalizedKeyword === '' ? '전체' : '검색 결과' }} <b>{{ filteredMedicines.length }}</b>개
                    <span v-if="innerSelected.length > 0" class="summary-selected"> · {{ innerSelected.length }}개 선택</span>
                </span>
                <button type="button" class="reset-button" :disabled="innerSelected.length === 0" @click="reset">선택 초기화</button>
            </div>

            <div class="list">
                <div v-if="isLoading">
                    <div v-for="n in 8" :key="`skeleton_${n}`" class="skeleton-row">
                        <span class="skeleton skeleton-check"></span>
                        <span class="skeleton skeleton-text" :style="`width: ${35 + (n * 17) % 40}%;`"></span>
                    </div>
                </div>
                <div v-else-if="isError" class="empty">
                    <span class="empty-title">목록을 불러오지 못했어요</span>
                    <span class="empty-description">잠시 후 다시 시도해 주세요.</span>
                    <button type="button" class="retry-button" @click="fetchMedicines">다시 시도</button>
                </div>
                <div v-else-if="filteredMedicines.length === 0" class="empty">
                    <span v-if="normalizedKeyword === ''" class="empty-title">등록된 인슐린이 없어요</span>
                    <template v-else>
                        <span class="empty-title">‘{{ keyword.trim() }}’ 검색 결과가 없어요</span>
                        <span class="empty-description">인슐린 이름을 다시 확인해 주세요.</span>
                    </template>
                </div>
                <template v-else>
                    <div
                        v-for="name in filteredMedicines"
                        :key="name"
                        :class="['item', { 'item-active': isSelected(name) }]"
                        role="checkbox"
                        tabindex="0"
                        :aria-checked="isSelected(name) ? 'true' : 'false'"
                        @click="toggle(name)"
                        @keydown.enter.prevent="toggle(name)"
                        @keydown.space.prevent="toggle(name)"
                    >
                        <span :class="['check', { 'check-active': isSelected(name) }]">
                            <svg v-if="isSelected(name)" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M3 7.2l2.7 2.7L11 4.5" stroke="#FFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </span>
                        <span class="item-name">
                            <span
                                v-for="(part, index) in highlight(name)"
                                :key="index"
                                :class="{ 'item-match': part.match }"
                            >{{ part.text }}</span>
                        </span>
                    </div>
                </template>
            </div>

        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Medicine from '@/utilities/medicine';

export default {
    name: 'insulinSelectModal',
    props: ['selected'],
    data: function()
    {
        return {
            medicines: [],
            innerSelected: (this.selected || []).slice(),
            keyword: '',
            isLoading: true,
            isError: false,
            // 선택된 약품 칩 마우스 드래그
            chipDrag: {
                startX: 0,
                startScrollLeft: 0,
                isDown: false,
                isDragging: false,
                suppressClick: false
            },
            chipEdges: {
                left: false,
                right: false
            }
        }
    },
    computed: {
        normalizedKeyword: function()
        {
            return this.normalize(this.keyword);
        },
        // 검색은 불러온 목록 안에서만 (공백, 대소문자 무시)
        filteredMedicines: function()
        {
            if(this.normalizedKeyword === '')
            {
                return this.medicines;
            }
            return this.medicines.filter((name) => {
                return this.normalize(name).indexOf(this.normalizedKeyword) > -1;
            });
        }
    },
    mounted: function()
    {
        document.body.style.overflow = 'hidden'; // 스크롤 비활성화
        document.addEventListener('keydown', this.handleKeydown);
        this.$nextTick(this.updateChipEdges);
        this.fetchMedicines();
    },
    beforeDestroy: function()
    {
        document.body.style.overflow = ''; // 스크롤 활성화
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('mousemove', this.onChipMouseMove);
        document.removeEventListener('mouseup', this.onChipMouseUp);
    },
    methods: {
        fetchMedicines: async function()
        {
            this.isLoading = true;
            this.isError = false;
            try
            {
                const url = `${process.env.API_DB_ROOT}/diabetes/pharmacy/medicines`;
                const response = await axios.get(url);
                if(response.data.status !== 'success' || !Array.isArray(response.data.data))
                {
                    throw new Error('fetch medicines failed');
                }
                // 바크시미는 별도 체크박스로 선택하므로 인슐린 목록에서 제외
                this.medicines = response.data.data.filter((name) => !Medicine.hasBaqsimi(name));
            }
            catch(err)
            {
                this.isError = true;
            }
            finally
            {
                this.isLoading = false;
            }
        },
        normalize: function(text)
        {
            return String(text).replace(/\s/g, '').toLowerCase();
        },
        // 검색어와 일치하는 부분을 강조하기 위해 약품명을 나눈다
        highlight: function(name)
        {
            const index = this.normalizedKeyword === '' ? -1 : name.toLowerCase().indexOf(this.normalizedKeyword);
            if(index < 0)
            {
                return [{ text: name, match: false }];
            }

            const end = index + this.normalizedKeyword.length;
            return [
                { text: name.substring(0, index), match: false },
                { text: name.substring(index, end), match: true },
                { text: name.substring(end), match: false }
            ].filter((part) => part.text !== '');
        },
        isSelected: function(name)
        {
            return this.innerSelected.indexOf(name) > -1;
        },
        toggle: function(name)
        {
            const index = this.innerSelected.indexOf(name);
            if(index > -1)
            {
                this.innerSelected.splice(index, 1);
                this.$nextTick(this.updateChipEdges);
            }
            else
            {
                this.innerSelected.push(name);
                // 새로 추가된 칩이 보이도록 끝으로 이동
                this.$nextTick(() => {
                    const list = this.$refs.selectedList;
                    if(list)
                    {
                        list.scrollTo({ left: list.scrollWidth, behavior: 'smooth' });
                        this.updateChipEdges();
                    }
                });
            }
        },
        updateChipEdges: function()
        {
            const list = this.$refs.selectedList;
            if(!list)
            {
                this.chipEdges = { left: false, right: false };
                return;
            }
            this.chipEdges = {
                left: list.scrollLeft > 1,
                right: list.scrollLeft + list.clientWidth < list.scrollWidth - 1
            };
        },
        onChipMouseDown: function(event)
        {
            if(event.button !== 0)
            {
                return;
            }
            const list = this.$refs.selectedList;
            this.chipDrag.startX = event.clientX;
            this.chipDrag.startScrollLeft = list.scrollLeft;
            this.chipDrag.isDown = true;
            this.chipDrag.isDragging = false;
            // 목록 밖으로 나가도 계속 끌 수 있도록 document 에 등록
            document.addEventListener('mousemove', this.onChipMouseMove);
            document.addEventListener('mouseup', this.onChipMouseUp);
        },
        onChipMouseMove: function(event)
        {
            if(!this.chipDrag.isDown)
            {
                return;
            }
            const dx = event.clientX - this.chipDrag.startX;
            // 살짝 움직인 건 클릭으로 본다
            if(!this.chipDrag.isDragging && Math.abs(dx) > 4)
            {
                this.chipDrag.isDragging = true;
            }
            if(this.chipDrag.isDragging)
            {
                event.preventDefault(); // 텍스트 선택 방지
                this.$refs.selectedList.scrollLeft = this.chipDrag.startScrollLeft - dx;
            }
        },
        onChipMouseUp: function()
        {
            // 끌었다 놓은 경우 칩 클릭(선택 해제)으로 처리되지 않게 한다
            this.chipDrag.suppressClick = this.chipDrag.isDragging;
            this.chipDrag.isDown = false;
            this.chipDrag.isDragging = false;
            document.removeEventListener('mousemove', this.onChipMouseMove);
            document.removeEventListener('mouseup', this.onChipMouseUp);
        },
        onChipClickCapture: function(event)
        {
            if(this.chipDrag.suppressClick)
            {
                event.stopPropagation();
                event.preventDefault();
                this.chipDrag.suppressClick = false;
            }
        },
        // 마우스 휠(세로)로도 좌우 이동
        onChipWheel: function(event)
        {
            const list = this.$refs.selectedList;
            if(Math.abs(event.deltaY) > Math.abs(event.deltaX) && list.scrollWidth > list.clientWidth)
            {
                event.preventDefault();
                list.scrollLeft += event.deltaY;
            }
        },
        reset: function()
        {
            this.innerSelected = [];
        },
        // 모달을 열 때와 비교해 선택이 바뀌었는지 (순서는 무시)
        isSelectionChanged: function()
        {
            const before = (this.selected || []).slice().sort();
            const after = this.innerSelected.slice().sort();
            return before.length !== after.length || before.some((name, index) => name !== after[index]);
        },
        handleKeydown: function(event)
        {
            if(event.key === 'Escape')
            {
                this.close();
            }
        },
        close: function()
        {
            const isChanged = this.isSelectionChanged();
            this.$emit('close');
            // 완료 버튼 없이 닫을 때 반영. 선택이 바뀐 경우에만 알려서 재검색하게 한다
            if(isChanged)
            {
                this.$emit('change', this.innerSelected.slice());
            }
        }
    }
}
</script>

<style scoped>
    .wrapper {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 200;
        display: flex;
        justify-content: center;
        align-items: flex-end;
    }
    .dim {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        animation: fade-in 0.2s ease-out;
    }
    .sheet {
        position: relative;
        width: 100%;
        max-width: 600px;
        height: 85vh;
        display: flex;
        flex-direction: column;
        background: var(--Gray-00, #FFF);
        border-radius: 20px 20px 0 0;
        box-sizing: border-box;
        overflow: hidden;
        animation: slide-up 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
    }

    /* header */
    .header {
        flex-shrink: 0;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 28px 20px 0 20px;
    }
    .col {
        display: flex;
        flex-direction: column;
    }
    .title {
        color: var(--Gray-100, #1E1E1E);
        font-family: Pretendard;
        font-size: 24px;
        font-style: normal;
        font-weight: 600;
        line-height: 28px;
        letter-spacing: -0.48px;
    }
    .subtitle {
        margin-top: 6px;
        color: var(--Gray-60, #6B7684);
        font-family: Pretendard;
        font-size: 15px;
        font-weight: 400;
        line-height: 20px;
    }
    .close-button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 36px;
        height: 36px;
        margin: -4px -8px 0 0;
        padding: 0;
        border: none;
        border-radius: 50%;
        background: transparent;
        cursor: pointer;
    }

    /* search */
    .search {
        flex-shrink: 0;
        position: relative;
        margin: 20px 20px 0 20px;
        color: var(--Gray-50, #A3AAB6);
    }
    .search:focus-within {
        color: #184FA1;
    }
    .search-icon {
        position: absolute;
        top: 50%;
        left: 14px;
        transform: translateY(-50%);
        pointer-events: none;
    }
    .search-input {
        width: 100%;
        height: 48px;
        padding: 0 44px;
        box-sizing: border-box;
        border: 1px solid transparent;
        border-radius: 6px;
        background: var(--Gray-20, #F3F5F8);
        color: var(--Gray-100, #1E1E1E);
        font-family: Pretendard;
        font-size: 16px;
        font-weight: 500;
        outline: none;
        -webkit-appearance: none;
        transition: border-color 0.15s, background-color 0.15s;
    }
    .search-input::placeholder {
        color: var(--Gray-50, #A3AAB6);
        font-weight: 400;
    }
    .search-input:focus {
        border-color: #184FA1;
        background: var(--Gray-00, #FFF);
    }
    .search-input:disabled {
        opacity: 0.6;
    }
    .clear-button {
        position: absolute;
        top: 50%;
        right: 8px;
        transform: translateY(-50%);
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
    }

    /* selected chips */
    .selected-wrap {
        flex-shrink: 0;
        position: relative;
        margin-top: 12px;
    }
    .selected-list {
        display: flex;
        gap: 6px;
        padding: 0 20px;
        overflow-x: auto;
        white-space: nowrap;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior-x: contain; /* 옆으로 넘길 때 브라우저 뒤로가기 방지 */
        touch-action: pan-x;
        cursor: grab;
        user-select: none;
    }
    .selected-list::-webkit-scrollbar {
        display: none;
    }
    .selected-list-dragging {
        cursor: grabbing;
    }
    .selected-list-dragging .selected-chip {
        cursor: grabbing;
    }
    .selected-list::after {
        /* 마지막 칩 오른쪽 여백 (flex 컨테이너는 padding-right 가 스크롤 영역에 포함되지 않음) */
        content: '';
        flex-shrink: 0;
        width: 14px;
    }
    .edge {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 28px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s;
    }
    .edge-visible {
        opacity: 1;
    }
    .edge-left {
        left: 0;
        background: linear-gradient(90deg, #FFF 20%, rgba(255, 255, 255, 0));
    }
    .edge-right {
        right: 0;
        background: linear-gradient(270deg, #FFF 20%, rgba(255, 255, 255, 0));
    }
    .selected-chip {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 4px;
        height: 32px;
        padding: 0 10px 0 12px;
        border: none;
        border-radius: 100px;
        background: #EAF1FB;
        color: #184FA1;
        font-family: Pretendard;
        font-size: 14px;
        font-weight: 600;
        line-height: 18px;
        cursor: pointer;
    }

    /* summary */
    .summary {
        flex-shrink: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 16px 20px 0 20px;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--gray-30-stroke, #E8EAEE);
        color: var(--Gray-60, #6B7684);
        font-family: Pretendard;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
    }
    .summary b {
        color: var(--Gray-100, #1E1E1E);
        font-weight: 600;
    }
    .summary-selected {
        color: #184FA1;
        font-weight: 600;
    }
    .reset-button {
        padding: 0;
        border: none;
        background: transparent;
        color: var(--Gray-60, #6B7684);
        font-family: Pretendard;
        font-size: 14px;
        font-weight: 500;
        text-decoration: underline;
        text-underline-offset: 3px;
        cursor: pointer;
    }
    .reset-button:disabled {
        color: var(--gray-40-button-stroke, #D1D6DB);
        text-decoration: none;
        cursor: default;
    }

    /* list */
    .list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        padding: 6px 8px calc(16px + env(safe-area-inset-bottom)) 8px;
    }
    .item {
        display: flex;
        align-items: center;
        min-height: 52px;
        padding: 0 12px;
        border-radius: 8px;
        cursor: pointer;
        outline: none;
        -webkit-tap-highlight-color: transparent;
        transition: background-color 0.15s;
    }
    .item:active {
        background: var(--Gray-20, #F3F5F8);
    }
    /* focus-visible 미지원 브라우저에서 :active 규칙까지 무시되지 않도록 분리 */
    .item:focus-visible {
        background: var(--Gray-20, #F3F5F8);
    }
    @media (hover: hover) {
        .item:hover {
            background: var(--Gray-20, #F3F5F8);
        }
    }
    .check {
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 22px;
        height: 22px;
        box-sizing: border-box;
        border: 1.5px solid #C5CBD3;
        border-radius: 6px;
        background: var(--Gray-00, #FFF);
        transition: background-color 0.15s, border-color 0.15s;
    }
    .check-active {
        border-color: #184FA1;
        background: #184FA1;
    }
    .item-name {
        margin-left: 12px;
        color: var(--Gray-100, #1E1E1E);
        font-family: Pretendard;
        font-size: 17px;
        font-weight: 500;
        line-height: 22px;
        word-break: keep-all;
    }
    .item-active .item-name {
        color: #184FA1;
        font-weight: 600;
    }
    .item-match {
        color: #184FA1;
        font-weight: 700;
    }

    /* loading */
    .skeleton-row {
        display: flex;
        align-items: center;
        height: 52px;
        padding: 0 12px;
    }
    .skeleton {
        display: block;
        border-radius: 6px;
        background: linear-gradient(90deg, #F3F5F8 25%, #E8EAEE 50%, #F3F5F8 75%);
        background-size: 200% 100%;
        animation: shimmer 1.2s ease-in-out infinite;
    }
    .skeleton-check {
        flex-shrink: 0;
        width: 22px;
        height: 22px;
    }
    .skeleton-text {
        height: 16px;
        margin-left: 12px;
    }

    /* empty, error */
    .empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 64px 20px;
        text-align: center;
    }
    .empty-title {
        color: var(--Gray-100, #1E1E1E);
        font-family: Pretendard;
        font-size: 17px;
        font-weight: 600;
        line-height: 22px;
        word-break: keep-all;
    }
    .empty-description {
        margin-top: 6px;
        color: var(--Gray-60, #6B7684);
        font-family: Pretendard;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
    }
    .retry-button {
        margin-top: 20px;
        height: 40px;
        padding: 0 18px;
        border: 1px solid var(--gray-40-button-stroke, #D1D6DB);
        border-radius: 100px;
        background: var(--Gray-00, #FFF);
        color: var(--Gray-100, #1E1E1E);
        font-family: Pretendard;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
    }

    @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    @keyframes slide-up {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    }
    @keyframes shimmer {
        from { background-position: 200% 0; }
        to { background-position: -200% 0; }
    }
</style>



// WEBPACK FOOTER //
// src/components/modals/InsulinSelectModal.vue